import {
    AccountModel,
    BrandsFactory,
    Dropdown,
    EEvents,
    EStorageType,
    GenericWrapperContext,
    getBrandUrl,
    getStorage,
    Header,
    Icon,
    LinearProgress,
    LoadingContainer,
    Modal,
    setStorage,
    TBrand,
    TBrandEnrich,
    TEnrichPerson,
    trackEvent,
    Typography,
    UserModel
} from "@cakemail-org/ui-components-v2"
import { useContext, useEffect, useState } from "react"
import { getLanguagesOptions } from "../../data/language"
import { EOnboardingStep, TOnboarding, TOnboardingIndustryForm, TStepMap } from "./types"
import { SelectChangeEvent, Stack } from "@mui/material"
import { OnboardingContext } from "./context"
import { useTranslation } from "react-i18next"
import "./styles.scss"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { getStepMap } from "./data"
import { PUBLIC_DOMAINS } from "../../data/publicDomains"
import { EStorageKeys } from "../../types/storage"

export function Onboarding({ setUser, onOnboardingCompleted }: TOnboarding) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const [step, setStep] = useState<EOnboardingStep>(EOnboardingStep.user)
    const [userForm, setUserForm] = useState<UserModel>(genericWrapper.user)
    const [industryForm, setIndustryForm] = useState<TOnboardingIndustryForm>({})
    const [companyForm, setCompanyForm] = useState<AccountModel>(genericWrapper.account)
    const [isAgency, setIsAgency] = useState<boolean>(false)
    const [brandForm, setBrandForm] = useState<Partial<TBrand>>({})
    const [enrichedPerson, setEnrichedPerson] = useState<TEnrichPerson | null>()
    const [enrichedBrand, setEnrichedBrand] = useState<TBrandEnrich | null>()


    useEffect(() => {

        const enrichedPersonStorage = getStorage({ key: EStorageKeys.onboardingEnrichedPerson, type: EStorageType.session })
        const enrichedBrandStorage = getStorage({ key: EStorageKeys.onboardingEnrichedBrand, type: EStorageType.session })

        if (enrichedBrandStorage) {
            setEnrichedBrand(enrichedBrandStorage)
        }

        if (enrichedPersonStorage) {
            setEnrichedPerson(enrichedPersonStorage)
            setStep(EOnboardingStep.foundMatch)
        } else {
            BrandsFactory.enrichEmail({ email: genericWrapper.user.email }).then((res) => {
                setEnrichedPerson(res.person)
                setStorage({ key: EStorageKeys.onboardingEnrichedPerson, data: res.person, type: EStorageType.session })
                setStep(EOnboardingStep.foundMatch)
            }).catch(() => {
                setEnrichedPerson(null)
            })

            const emailDomain = genericWrapper.user.email.split('@')[1]
            if (!PUBLIC_DOMAINS.includes(emailDomain)) {
                BrandsFactory.enrichBrand({ domain: emailDomain }).then((res) => {
                    setEnrichedBrand(res.brand)
                    setStorage({ key: EStorageKeys.onboardingEnrichedBrand, data: res.brand, type: EStorageType.session })
                }).catch(() => {
                    setEnrichedBrand(null)
                })
            } else {
                setEnrichedBrand(null)
            }
        }

    }, [])


    const stringParams = {
        name: userForm.first_name || genericWrapper.user.first_name,
        companyName: enrichedPerson?.organization?.name,
        resellerName: genericWrapper.partnerBrand.name
    }

    const stepMap: TStepMap = getStepMap(stringParams, t, enrichedPerson)
    const totalSteps: number = Object.keys(stepMap).length - 1

    const currentStep = stepMap[step]

    function handleLanguageChange(event: SelectChangeEvent<string>) {
        genericWrapper.user.language = event.target.value
        genericWrapper.user.update().then((newUser) => {
            setUser(newUser)
        })
    }

    function handleBack() {
        if (currentStep.name === "completed") {
            return
        }
        const entries = Object.entries(stepMap)
        for (const [key, value] of entries) {
            if (value.id === currentStep.id - 1) {
                let currentStep = key as EOnboardingStep
                if (key === EOnboardingStep.manageAccountsConfirmation && !isAgency) {
                    currentStep = entries[value.id - 2][1].name as EOnboardingStep
                }
                setStep(currentStep)

                trackEvent(EEvents.BUTTON_CLICKED, {
                    context: "Onboarding Back Button Clicked",
                    extendedContext: {
                        step: currentStep
                    }
                })
                break
            }
        }
    }
    function handleNext(btnRef?: TButtonRef) {
        const entries = Object.entries(stepMap)
        for (const [key, value] of entries) {
            if (value.id === currentStep.id + 1) {
                let currentStep = key as EOnboardingStep
                if (currentStep === EOnboardingStep.completed) {
                    btnRef && btnRef?.animate()
                    onOnboardingCompleted({
                        userForm,
                        industryForm,
                        companyForm,
                        brandForm,
                        isAgency
                    }).then(() => {
                        trackEvent(EEvents.ACCOUNT_SETUP_FORM, {
                            first_name: userForm.first_name,
                            last_name: userForm.last_name,
                            industry: industryForm.industry,
                            sector: industryForm.sector,
                            teamSize: industryForm.teamSize
                        })
                        trackEvent(EEvents.ONBOARDING_COMPLETED)
                        btnRef && btnRef?.reset()
                        setStep(currentStep as EOnboardingStep)
                    }).catch(() => {
                        trackEvent(EEvents.ONBOARDING_COMPLETED, {
                            withError: true,
                        })
                        btnRef && btnRef?.reset()
                        setStep(currentStep as EOnboardingStep)
                    })
                } else {
                    if (currentStep === "manageAccountsConfirmation" && !["market_research", "marketing_and_advertising"].includes(industryForm.sector || "")) {
                        setIsAgency(false)
                        currentStep = entries[value.id][1].name as EOnboardingStep
                    }

                    setStep(currentStep as EOnboardingStep)
                }

                trackEvent(EEvents.BUTTON_CLICKED, {
                    context: "Onboarding Continue Button Clicked",
                    extendedContext: {
                        step: currentStep
                    }
                })
                break
            }
        }
    }

    const isCompletedStep = currentStep.name === "completed"

    let hasEnrichmentData: boolean = Boolean(enrichedPerson?.email)

    switch (step) {
        case "user":
            hasEnrichmentData = Boolean(enrichedPerson?.email && enrichedPerson?.first_name && enrichedPerson?.last_name)
            break
    }

    return <Modal
        open={true}
        variant="spacedHeader"
        className="onboarding-modal"
        heading={{
            logoUrl: getBrandUrl({ elKey: "logo", brand: genericWrapper.partnerBrand }),
            actions: <Dropdown
                key={1}
                iconOnly
                chevronIconOverride="Globe24"
                variant="standard"
                options={getLanguagesOptions({ partnerBrand: genericWrapper.partnerBrand })}
                prefixIcon={"LinkArrow18"}
                onChange={handleLanguageChange}
                value={genericWrapper.user.language}
            />
        }}
        content={<OnboardingContext.Provider value={{
            userForm,
            setUserForm,
            industryForm,
            setIndustryForm,
            companyForm,
            setCompanyForm,
            brandForm,
            setBrandForm,
            onOnboardingCompleted,
            step,
            setStep,
            enrichedPerson,
            setEnrichedPerson,
            enrichedBrand,
            setEnrichedBrand,
            handleNext,
            isAgency,
            setIsAgency
        }}>
            <LoadingContainer loading={enrichedPerson === undefined}>
                <Stack direction="column" className="onboardingContent" gap={8}>
                    <Stack direction="row" className="onboardingProgress" alignItems="center" gap={6}>

                        {currentStep.id > 1 && <>
                            {(currentStep.name === "user" && (!enrichedPerson || !enrichedPerson?.organization)) ? "" : <Icon className={isCompletedStep ? "success-icon" : "arrow-icon"} color={isCompletedStep ? "secondary" : "shade1000"} name={isCompletedStep ? "Success24" : "Arrow18"} onClick={handleBack} />}
                        </>}
                        {isCompletedStep ?
                            <Typography variant="body1L" className="semiBold" >{t("onboarding-completed-progress-title")}</Typography>
                            :
                            <LinearProgress variant="determinate" color="secondary" size="medium" value={100 * currentStep.id / totalSteps} />
                        }

                    </Stack>

                    <Header
                        heading={hasEnrichmentData ? currentStep.enrichTitle || currentStep.title : currentStep.title}
                        headingVariant="displayL"
                        desc={hasEnrichmentData ? currentStep.enrichDesc || currentStep.desc : currentStep.desc}
                    />

                    <Stack direction="row" className="onboardingContent" >
                        {currentStep.component}
                    </Stack>
                </Stack>
            </LoadingContainer>
        </OnboardingContext.Provider>}
    />
}